import { ReactNode } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { Button, Spinner } from '../ui'
import { useUser } from '../../contexts/UserContext'
import { tokenManager } from '../../lib/api'

/**
 * Renders its children only once the signed-in user has loaded.
 *
 * A token in localStorage isn't enough: until /api/users/me/ comes back,
 * everything below would render with `user = null` and fall back to
 * placeholders ("User", "Unable to load user data") or skip role checks.
 */
export function ProtectedRoute({ children }: { children: JSX.Element }) {
  const { user, loading } = useUser()

  if (!tokenManager.isAuthenticated()) {
    return <Navigate to="/" replace />
  }
  // Only block on the first load of a session. A refetch of the same user
  // (e.g. after saving the profile) keeps the app mounted.
  if (loading && !user) {
    return <FullPageSpinner />
  }
  if (!user) {
    return <UserLoadFailed />
  }
  return children
}

/** Renders children only for a loaded, signed-in user; nothing otherwise. */
export function SignedInOnly({ children }: { children: ReactNode }) {
  const { user } = useUser()
  return user && tokenManager.isAuthenticated() ? <>{children}</> : null
}

export function FullPageSpinner() {
  return (
    <div className="flex h-screen items-center justify-center bg-background" role="status" aria-label="Loading">
      <Spinner size="lg" />
    </div>
  )
}

// The token exists but /me failed for a reason other than an expired session
// (that path clears the token and redirects on its own) — backend down, 5xx,
// network drop. Offer a retry rather than rendering the app without a user.
function UserLoadFailed() {
  const { refetch, logout } = useUser()
  const navigate = useNavigate()

  const signOut = () => {
    logout()
    navigate('/', { replace: true })
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 bg-background p-4 text-center">
      <p className="text-foreground">We couldn't load your account.</p>
      <div className="flex gap-2">
        <Button onClick={() => { refetch() }}>Try again</Button>
        <Button variant="outline" onClick={signOut}>Sign out</Button>
      </div>
    </div>
  )
}
